import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSessionState = useCallback(() => {
    localStorage.removeItem("pos_token");
    setUser(null);
    setTenant(null);
    setSubscription(null);
    setPlan(null);
    setEntitlements(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      // HttpOnly session cookie is authoritative. The request also supports a
      // legacy Authorization header through api.js during rolling migration.
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      setTenant(r.data.tenant);
      setSubscription(r.data.subscription);
      setPlan(r.data.plan || null);
      setEntitlements(r.data.entitlements || null);
      // A successful cookie-authenticated request proves localStorage JWT is no longer needed.
      localStorage.removeItem("pos_token");
      return r.data;
    } catch {
      clearSessionState();
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearSessionState]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onExpired = () => clearSessionState();
    window.addEventListener("kraya-auth-expired", onExpired);
    return () => window.removeEventListener("kraya-auth-expired", onExpired);
  }, [clearSessionState]);

  const establishSession = async (data) => {
    // Never persist the JSON JWT. Backend sets kraya_session as HttpOnly cookie.
    localStorage.removeItem("pos_token");
    if (data?.user) setUser(data.user);
    if (Object.prototype.hasOwnProperty.call(data || {}, "tenant")) setTenant(data.tenant || null);
    await refresh();
    return data;
  };

  const login = async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    if (r.data?.requires_2fa) return r.data;
    return establishSession(r.data);
  };

  const verifyTwoFactor = async (challengeToken, code) => {
    const r = await api.post("/auth/2fa/verify-login", {
      challenge_token: challengeToken,
      code,
    });
    return establishSession(r.data);
  };

  const register = async (data) => {
    const r = await api.post("/auth/register", data);
    if (r.data?.token) await establishSession(r.data);
    return r.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Local state must still be cleared even if network logout fails.
    } finally {
      clearSessionState();
      window.location.href = "/login";
    }
  };

  const hasPerm = (permission) => {
    if (!user) return false;
    if (user.role === "platform_owner" || user.role === "admin") return true;
    const perms = user.permissions || [];
    return perms.includes("*") || perms.includes(permission);
  };

  const hasFeature = (featureKey) => {
    if (!featureKey) return true;
    if (!user) return false;
    if (user.role === "platform_owner") return true;
    if (!entitlements?.available) return false;
    return (entitlements.feature_keys || []).includes(featureKey);
  };

  return (
    <AuthCtx.Provider value={{
      user, tenant, subscription, plan, entitlements, loading,
      login, verifyTwoFactor, register, logout, refresh, hasPerm, hasFeature,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
